import { create } from 'zustand'
import { contentApi, subscriptionApi, type ContentAccess, type ContentType } from '@/services/api'

// Reconcile with the store once per app session, before the first access
// snapshot, so a renewal/cancel/expiry is reflected immediately on launch.
let reconciledThisSession = false

/**
 * Content-access store. The backend is the source of truth for the
 * hard-paywall trial model; this caches the latest snapshot and exposes
 * helpers the UI gates off. Not persisted — always re-fetched on load so a
 * stale client can't grant access after a trial expires.
 */

export type GateState =
  | 'premium' // paid: everything, no limits
  | 'unlocked' // trial: this item already spent a credit
  | 'unlockable' // trial: locked but credits remain
  | 'no-credits' // trial: locked and out of credits
  | 'expired' // trial ended, not subscribed: everything locked
  | 'loading'

interface AccessState {
  access: ContentAccess | null
  loading: boolean
  fetchAccess: () => Promise<void>
  unlock: (type: ContentType, id: string) => Promise<{ ok: boolean; reason?: string }>
}

export const useAccessStore = create<AccessState>((set, get) => ({
  access: null,
  loading: false,

  fetchAccess: async () => {
    set({ loading: true })
    try {
      if (!reconciledThisSession) {
        reconciledThisSession = true
        // Best-effort: never block gating on a store hiccup. The backend
        // no-ops this for trial/review accounts.
        try { await subscriptionApi.refresh() } catch { /* ignore */ }
      }
      const { data } = await contentApi.getAccess()
      set({ access: data, loading: false })
    } catch {
      // Network/401 (e.g. local dev token): leave null; gateState() treats
      // a missing snapshot as "loading" so the UI doesn't wrongly unlock.
      set({ loading: false })
    }
  },

  unlock: async (type, id) => {
    try {
      const { data } = await contentApi.unlock(type, id)
      set({ access: data })
      return { ok: true }
    } catch (e) {
      const err = e as { response?: { data?: { error?: { code?: string } } } }
      const code = err.response?.data?.error?.code
      // Refresh snapshot so the UI reflects the true state after a failure.
      get().fetchAccess()
      return { ok: false, reason: code }
    }
  },
}))

/** Pure helper: what gate applies to a given content item right now. */
export function gateState(
  access: ContentAccess | null,
  type: ContentType,
  id: string
): GateState {
  if (!access) return 'loading'
  if (access.access_level === 'premium') return 'premium'
  if (access.access_level === 'locked') return 'expired'
  // trial
  if (access.unlocked[type]?.includes(id)) return 'unlocked'
  const remaining = access.credits[type]?.remaining ?? 0
  return remaining > 0 ? 'unlockable' : 'no-credits'
}

/** Programs are premium-only — never unlockable on the trial. */
export function programGateState(access: ContentAccess | null): GateState {
  if (!access) return 'loading'
  if (access.access_level === 'premium') return 'premium'
  if (access.access_level === 'locked') return 'expired'
  return 'no-credits' // trial: visible but locked, no credits ever apply
}
